import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Archive, Check, Close, Edit, Logout, PersonAddAlt1, PersonRemove, Search, Shield } from "@mui/icons-material";
import ConfirmModal from "../modals/ConfirmModal";
import { colors, typography } from "../../theme/designSystem";
import { chatService, ChatContact, ChatGroup, ChatGroupDetail } from "../../services/chatService";

// Membros do grupo (RN-551h): quem está dentro, quem administra, quem está online, e Sair. Para o
// administrador do grupo (fase 2): renomear, adicionar e remover pessoas, tornar outro membro
// administrador e arquivar. O Administrador da clínica não tem passe livre: só quem administra o grupo.

const TITLE_MAX = 60;

const initials = (name: string) =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("");

const PERSON_COLORS = ["#5C7CFA", "#845EF7", "#F76707", "#E64980", "#0CA678", "#F59F00", "#1098AD", "#7048E8"];
const personColor = (id: string): string => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PERSON_COLORS[h % PERSON_COLORS.length];
};

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { description?: string } } })?.response?.data?.description || fallback;

interface Props {
  open: boolean;
  threadId: string;
  title: string;
  /** Contatos do chat (sem o Sininho), para o administrador adicionar gente */
  contacts: ChatContact[];
  photos: Record<string, string>;
  onClose: () => void;
  onLeft: (threadId: string) => void;
  /** Título ou quantidade de membros mudou: a lista e o cabeçalho acompanham */
  onChanged: (group: ChatGroup) => void;
  /** Grupo arquivado: some da lista de todos */
  onArchived: (threadId: string) => void;
}

type Pending = { kind: "leave" } | { kind: "remove"; userId: string; name: string } | { kind: "promote"; userId: string; name: string } | { kind: "archive" } | null;

const GroupMembersModal: React.FC<Props> = ({ open, threadId, title, contacts, photos, onClose, onLeft, onChanged, onArchived }) => {
  const [detail, setDetail] = useState<ChatGroupDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(title);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<string[]>([]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await chatService.group(threadId));
    } catch {
      setError("Não foi possível carregar os membros.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setEditingTitle(false);
    setTitleDraft(title);
    setAdding(false);
    setPicked([]);
    setSearch("");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, threadId]);

  const members = useMemo(() => detail?.members ?? [], [detail]);
  const isAdmin = detail?.group.myRole === "admin";
  const memberIds = useMemo(() => new Set(members.map((m) => m.userId)), [members]);
  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter((c) => !memberIds.has(c.userId) && (!q || c.name.toLowerCase().includes(q)));
  }, [contacts, memberIds, search]);

  const saveTitle = async () => {
    const next = titleDraft.trim();
    if (!next || next === title) {
      setEditingTitle(false);
      setTitleDraft(title);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const group = await chatService.renameGroup(threadId, next);
      onChanged(group);
      setEditingTitle(false);
      await load();
    } catch (e) {
      setError(errorText(e, "Não foi possível renomear o grupo."));
    } finally {
      setBusy(false);
    }
  };

  const addMembers = async () => {
    if (picked.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const next = await chatService.addGroupMembers(threadId, picked);
      setDetail(next);
      onChanged(next.group);
      setAdding(false);
      setPicked([]);
      setSearch("");
    } catch (e) {
      setError(errorText(e, "Não foi possível adicionar as pessoas."));
    } finally {
      setBusy(false);
    }
  };

  const confirmPending = async () => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      if (pending.kind === "leave") {
        await chatService.leaveGroup(threadId);
        setPending(null);
        onLeft(threadId);
        return;
      }
      if (pending.kind === "archive") {
        await chatService.archiveGroup(threadId);
        setPending(null);
        onArchived(threadId);
        return;
      }
      if (pending.kind === "remove") await chatService.removeGroupMember(threadId, pending.userId);
      if (pending.kind === "promote") await chatService.promoteGroupAdmin(threadId, pending.userId);
      setPending(null);
      const next = await chatService.group(threadId);
      setDetail(next);
      onChanged(next.group);
    } catch (e) {
      setError(errorText(e, "Não foi possível concluir a ação."));
      setPending(null);
    } finally {
      setBusy(false);
    }
  };

  const confirmTexts: Record<string, { title: string; message: string; button: string; danger: boolean }> = {
    leave: { title: "Sair do grupo", message: `Sair do grupo "${title}"? O grupo some da sua lista e você deixa de ler as mensagens. Para voltar, um administrador precisa incluir você de novo.`, button: "Sair", danger: true },
    remove: { title: "Remover do grupo", message: `Tirar ${pending?.kind === "remove" ? pending.name : ""} do grupo "${title}"? A pessoa deixa de ler as mensagens; um administrador pode incluí-la de novo.`, button: "Remover", danger: true },
    promote: { title: "Tornar administrador", message: `${pending?.kind === "promote" ? pending.name : ""} passa a administrar o grupo "${title}" junto com você: renomear, adicionar e remover pessoas e arquivar.`, button: "Tornar administrador", danger: false },
    archive: { title: "Arquivar grupo", message: `Arquivar o grupo "${title}"? Ele some da lista de todos os membros. As mensagens ficam guardadas na base da clínica até o prazo de retenção.`, button: "Arquivar", danger: true },
  };
  const confirm = pending ? confirmTexts[pending.kind] : null;

  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}>
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, padding: "1rem 1.5rem" }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {editingTitle ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <TextField
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveTitle();
                    if (e.key === "Escape") { setEditingTitle(false); setTitleDraft(title); }
                  }}
                  size="small"
                  autoFocus
                  inputProps={{ maxLength: TITLE_MAX, "aria-label": "Nome do grupo", style: { fontSize: "0.95rem", color: "#fff" } }}
                  sx={{ flex: 1, "& .MuiOutlinedInput-root": { bgcolor: "rgba(255,255,255,0.15)", "& fieldset": { borderColor: "rgba(255,255,255,0.5)" } } }}
                />
                <IconButton size="small" onClick={saveTitle} disabled={busy} sx={{ color: "#fff" }} aria-label="Salvar nome"><Check fontSize="small" /></IconButton>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                <Typography component="div" sx={{ fontSize: "1.2rem", fontWeight: typography.fontWeight.semibold, lineHeight: 1.2 }} noWrap>{title}</Typography>
                {isAdmin && (
                  <Tooltip title="Renomear grupo">
                    <IconButton size="small" onClick={() => setEditingTitle(true)} sx={{ color: "#fff", opacity: 0.85 }} aria-label="Renomear grupo"><Edit sx={{ fontSize: 16 }} /></IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
            <Typography component="div" sx={{ fontSize: "0.78rem", opacity: 0.85 }}>
              {members.length} {members.length === 1 ? "membro" : "membros"}{isAdmin ? " · você administra" : ""}
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={busy} sx={{ color: colors.white, padding: "0.25rem" }} aria-label="Fechar"><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "0.5rem 0 0 !important" }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={22} sx={{ color: colors.primary }} /></Box>
          )}
          {error && <Typography sx={{ px: 3, py: 1, fontSize: "0.85rem", color: colors.error }}>{error}</Typography>}

          {!loading && !adding && members.map((m) => (
            <Box key={m.userId} sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 3, py: 0.85 }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                variant="dot"
                invisible={!m.online}
                sx={{ "& .MuiBadge-dot": { bgcolor: "#2ecc71", border: "2px solid #fff", width: 12, height: 12, borderRadius: "50%" } }}
              >
                <Avatar src={photos[m.userId]} sx={{ width: 38, height: 38, bgcolor: personColor(m.userId), color: "#fff", fontSize: "0.85rem", fontWeight: 600 }}>
                  {initials(m.name)}
                </Avatar>
              </Badge>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: colors.textPrimary }} noWrap>
                    {m.isMe ? `${m.name} (você)` : m.name}
                  </Typography>
                  {m.memberRole === "admin" && (
                    <Chip label="administra" size="small" sx={{ height: 18, fontSize: "0.62rem", fontWeight: 700, bgcolor: "#e6f7f9", color: colors.primary, "& .MuiChip-label": { px: 0.75 } }} />
                  )}
                </Box>
                <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary }} noWrap>
                  {m.role ?? "Equipe"}{m.online ? " · online" : ""}
                </Typography>
              </Box>
              {isAdmin && !m.isMe && (
                <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0 }}>
                  {m.memberRole !== "admin" && (
                    <Tooltip title="Tornar administrador">
                      <IconButton size="small" onClick={() => setPending({ kind: "promote", userId: m.userId, name: m.name })} disabled={busy} sx={{ color: colors.primary }} aria-label={`Tornar ${m.name} administrador`}>
                        <Shield sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Remover do grupo">
                    <IconButton size="small" onClick={() => setPending({ kind: "remove", userId: m.userId, name: m.name })} disabled={busy} sx={{ color: colors.error }} aria-label={`Remover ${m.name} do grupo`}>
                      <PersonRemove sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          ))}

          {adding && (
            <Box sx={{ px: 3, pb: 1 }}>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: colors.textPrimary, mb: 0.75 }}>Adicionar pessoas</Typography>
              <TextField
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar pessoa"
                size="small"
                fullWidth
                autoFocus
                inputProps={{ maxLength: 60, style: { fontSize: "0.88rem" } }}
                InputProps={{ startAdornment: (<InputAdornment position="start"><Search sx={{ fontSize: 18, color: colors.textSecondary }} /></InputAdornment>) }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "22px", bgcolor: "#f1f3f5", "& fieldset": { border: "none" } } }}
              />
              <Box sx={{ mt: 1, maxHeight: 260, overflowY: "auto", border: "1px solid #e9ecef", borderRadius: "10px" }}>
                {candidates.length === 0 && (
                  <Typography sx={{ px: 2, py: 2, textAlign: "center", fontSize: "0.85rem", color: colors.textSecondary }}>Ninguém para adicionar.</Typography>
                )}
                {candidates.map((c) => {
                  const checked = picked.includes(c.userId);
                  return (
                    <Box key={c.userId} onClick={() => setPicked((prev) => (checked ? prev.filter((x) => x !== c.userId) : [...prev, c.userId]))} sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.25, py: 0.6, cursor: "pointer", "&:hover": { bgcolor: "#f6f7f9" } }}>
                      <Checkbox checked={checked} size="small" sx={{ p: 0.25, color: colors.primary, "&.Mui-checked": { color: colors.primary } }} inputProps={{ "aria-label": c.name }} />
                      <Avatar src={photos[c.userId]} sx={{ width: 32, height: 32, bgcolor: personColor(c.userId), color: "#fff", fontSize: "0.75rem", fontWeight: 600 }}>{initials(c.name)}</Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: colors.textPrimary }} noWrap>{c.name}</Typography>
                        <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary }} noWrap>{c.role ?? "Equipe"}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.25 }}>
                <Button onClick={() => { setAdding(false); setPicked([]); }} disabled={busy} sx={{ textTransform: "none", color: colors.textSecondary }}>Cancelar</Button>
                <Button variant="contained" onClick={addMembers} disabled={busy || picked.length === 0} sx={{ textTransform: "none", bgcolor: colors.primary, "&:hover": { bgcolor: "#029AAB" } }}>
                  Adicionar {picked.length > 0 ? `(${picked.length})` : ""}
                </Button>
              </Box>
            </Box>
          )}

          {!loading && !adding && (
            <Box sx={{ px: 3, pt: 1.5, pb: 2, borderTop: "1px solid #f1f3f5", mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
              {isAdmin && (
                <Button variant="outlined" startIcon={<PersonAddAlt1 sx={{ fontSize: 16 }} />} onClick={() => setAdding(true)} disabled={busy} sx={{ textTransform: "none", borderColor: colors.primary, color: colors.primary }} fullWidth>
                  Adicionar pessoas
                </Button>
              )}
              <Button variant="outlined" color="error" startIcon={<Logout sx={{ fontSize: 16 }} />} onClick={() => setPending({ kind: "leave" })} disabled={busy} sx={{ textTransform: "none" }} fullWidth>
                Sair do grupo
              </Button>
              {isAdmin && (
                <Button startIcon={<Archive sx={{ fontSize: 16 }} />} onClick={() => setPending({ kind: "archive" })} disabled={busy} sx={{ textTransform: "none", color: colors.textSecondary }} fullWidth>
                  Arquivar grupo
                </Button>
              )}
              <Typography sx={{ fontSize: "0.72rem", color: colors.textSecondary }}>
                {isAdmin
                  ? "Quem administra renomeia, adiciona e remove pessoas, torna outros administradores e arquiva o grupo. Arquivar tira o grupo da lista de todos."
                  : "Ao sair, o grupo some da sua lista e você deixa de ler as mensagens. Os demais continuam."}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmModal
        isOpen={pending !== null}
        onCancel={() => setPending(null)}
        onConfirm={confirmPending}
        title={confirm?.title ?? ""}
        message={confirm?.message ?? ""}
        confirmButtonText={confirm?.button ?? "Confirmar"}
        type={confirm?.danger ? "danger" : "info"}
        isProcessing={busy}
      />
    </>
  );
};

export default GroupMembersModal;
